import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSearch, FiFileText, FiFolder, FiX } from 'react-icons/fi';
import { api } from '../api';

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.searchSections(searchQuery);
      setResults(response.data || []);
      setHasSearched(true);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Strip HTML and truncate content for preview
  const getContentPreview = (html, maxLength = 150) => {
    const text = html.replace(/<[^>]*>/g, '');
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Group results by category
  const groupedResults = results.reduce((acc, result) => {
    const categoryName = result.category_name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = [];
    }
    acc[categoryName].push(result);
    return acc;
  }, {});

  return (
    <div className="max-w-3xl mx-auto pb-24 sm:pb-8">
      <h1 className="text-2xl sm:text-3xl font-display text-lodge-brown-800 mb-6">
        Search
      </h1>

      {/* Search Input */}
      <div className="relative mb-8">
        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for information..."
          className="w-full pl-12 pr-12 py-3 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-lodge-brown-500 focus:border-lodge-brown-500 text-base"
          autoFocus
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
            aria-label="Clear search"
          >
            <FiX className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lodge-brown-600"></div>
        </div>
      )}

      {/* Results */}
      {!isLoading && hasSearched && (
        <>
          {results.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12 bg-white/80 backdrop-blur-sm rounded-xl"
            >
              <FiSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No results found for "{query}"</p>
              <p className="text-gray-400 text-sm mt-2">Try different keywords</p>
            </motion.div>
          ) : (
            <div className="space-y-6">
              <p className="text-sm text-gray-500">
                Found {results.length} result{results.length !== 1 ? 's' : ''}
              </p>

              {Object.entries(groupedResults).map(([categoryName, categoryResults]) => (
                <motion.div
                  key={categoryName}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {/* Category Header */}
                  <div className="flex items-center mb-3">
                    <FiFolder className="w-4 h-4 text-lodge-brown-500 mr-2" />
                    <span className="text-sm font-medium text-lodge-brown-700">
                      {categoryName}
                    </span>
                  </div>

                  {/* Results in Category */}
                  <div className="space-y-3">
                    {categoryResults.map((result) => (
                      <Link
                        key={result.id}
                        to={`/section/${result.slug}`}
                        className="block bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 border border-white/50"
                      >
                        <div className="flex items-start">
                          <div className="w-10 h-10 bg-lodge-brown-100 rounded-lg flex items-center justify-center shrink-0">
                            <FiFileText className="w-5 h-5 text-lodge-brown-600" />
                          </div>
                          <div className="ml-3 flex-grow min-w-0">
                            <h3 className="font-semibold text-mountain-gray-900 mb-1">
                              {result.title}
                            </h3>
                            <p className="text-sm text-gray-500 line-clamp-2">
                              {getContentPreview(result.content)}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Initial State */}
      {!isLoading && !hasSearched && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12 bg-white/80 backdrop-blur-sm rounded-xl"
        >
          <FiSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Start typing to search</p>
          <p className="text-gray-400 text-sm mt-2">
            Search through all guidebook sections
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default SearchPage;
